/**
 * The sign-in calls. The API client (and the Zod contracts it brings) loads
 * on first use, so the sign-in page itself stays within the public page's
 * JavaScript budget.
 */
import type {
  CodeSent,
  ConsumeCodeResult,
  CreateCodeRequest,
  Me,
  ResendCodeRequest,
  SignInMethods,
} from '@gradex-hire/contracts';

import type { paths } from '../../lib/api/schema.gen.js';

/** The body of `POST /api/v1/auth/code/consume`, as the API describes it. */
export type ConsumeBody =
  paths['/api/v1/auth/code/consume']['post']['requestBody']['content']['application/json'];

const client = () => import('../../lib/api/client.js').then((module) => module.api);

/**
 * Starts loading the API client before it is needed (when the person
 * focuses the address field), so sending the code does not wait for it.
 * Loading it on mount would put it in the page's critical path.
 */
export function preloadClient(): void {
  void client().catch(() => undefined);
}

/**
 * Which sign-in methods are offered (phone only while SMS sign-in is on).
 * A plain `fetch`, not the API client: the page asks on mount, and the
 * client must not load before the page has painted. Any failure means
 * "email only".
 */
export async function getMethods(fetchFn: typeof fetch = fetch): Promise<SignInMethods> {
  try {
    const response = await fetchFn('/api/v1/auth/methods', {
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
    });
    const body: unknown = response.ok ? await response.json() : undefined;
    const phone = (body as { phone?: unknown } | undefined)?.phone === true;
    return { email: true, phone };
  } catch {
    return { email: true, phone: false };
  }
}

/** Sends a code (and, by email, a one-tap link). */
export async function requestCode(body: CreateCodeRequest): Promise<CodeSent> {
  return (await client()).post('/api/v1/auth/code', { body });
}

/** Sends a new code for the same attempt. */
export async function resendCode(body: ResendCodeRequest): Promise<CodeSent> {
  return (await client()).post('/api/v1/auth/code/resend', { body });
}

/** Signs in with a code or a link code. */
export async function consumeCode(body: ConsumeBody): Promise<ConsumeCodeResult> {
  return (await client()).post('/api/v1/auth/code/consume', { body });
}

/** Remembers the signed-in account for the pages that follow, and says where to go. */
export async function afterSignIn(account: Me, next?: string): Promise<string> {
  const [{ getQueryClient }, { meQuery, destinationFor }] = await Promise.all([
    import('../../lib/api/query.js'),
    import('../../lib/session/session.js'),
  ]);
  getQueryClient().setQueryData(meQuery.queryKey, account);
  return destinationFor(account, next);
}

/** The person's time zone, for a new account. */
export function browserTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}
